import { v4 as uuidv4 } from 'uuid';
import { databaseService } from '@/services/DatabaseService';
import { loggingService } from '@/services/LoggingService';
import { analyticsService } from '@/services/AnalyticsService';
import { UserProfile, UserPreferences, HelperPermissions } from '@/types/UserProfile';
import { PROFILE_NAMES, PROFILE_TYPE, ProfileType, PROFILE_IDS } from '@/constants/profileConstants';
// Import the new services for Plus features
import { preferencesService } from '@/services/PreferencesService';
import { helperManagementService } from '@/services/HelperManagementService';

/**
 * Service responsible for managing user profiles
 * Core functionality only - Plus features moved to dedicated services
 */
export class UserProfileService {
  private readonly COLLECTION_NAME = 'user_profiles';
  private currentProfile: UserProfile | null = null;
  private profiles: UserProfile[] = [];
  private initialized = false;
  private initializing = false;
  private initPromise: Promise<void> | null = null;
  private readonly DEFAULT_TIMEOUT_MS = 10000; // 10 seconds timeout

  /**
   * Initialize the service and load all profiles
   * @param signal Optional AbortSignal to allow cancellation of initialization
   */
  async initialize(signal?: AbortSignal): Promise<void> {
    // Prevent multiple initializations
    if (this.initialized) {
      loggingService.debug('profile', 'UserProfileService already initialized');
      return;
    }
    
    // If initialization is already in progress, return the existing promise
    if (this.initializing && this.initPromise) {
      loggingService.debug('profile', 'UserProfileService initialization already in progress');
      return this.initPromise;
    }
    
    // Set initializing flag and create a new promise
    this.initializing = true;
    loggingService.info('profile', 'Starting UserProfileService initialization');
    
    // Create a single initialization promise that can be returned to all callers
    this.initPromise = this._initializeInternal(signal);
    return this.initPromise;
  }
  
  /**
   * Internal initialization method that does the actual work
   * This separation allows us to manage a single promise for multiple callers
   */
  private async _initializeInternal(signal?: AbortSignal): Promise<void> {
    // Create a cleanup function to ensure we always clean up timeouts
    const cleanupActions: Array<() => void> = [];
    
    try {
      // Shorter timeout to prevent UI hangs
      let timeoutId: NodeJS.Timeout;
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`Profile loading timed out after ${this.DEFAULT_TIMEOUT_MS/1000} seconds`));
        }, this.DEFAULT_TIMEOUT_MS);
        cleanupActions.push(() => clearTimeout(timeoutId));
      });
      
      // Set up signal handlers to cancel operations if component unmounts
      if (signal) {
        // If already aborted, throw immediately
        if (signal.aborted) {
          throw new Error('Profile loading aborted');
        }
        
        // Otherwise set up the abort listener
        const abortHandler = () => {
          cleanupActions.forEach(cleanup => cleanup());
          // No need to reject here, the promise will be aborted by the caller
        };
        signal.addEventListener('abort', abortHandler);
        cleanupActions.push(() => signal.removeEventListener('abort', abortHandler));
      }
      
      // Try to initialize database service first if not already initialized
      try {
        if (!databaseService.isInitialized()) {
          await databaseService.initialize();
        }
      } catch (dbError) {
        loggingService.warn('profile', `Database service initialization warning: ${dbError}`);
        // No direct console in production
        // Create a sensible default even if DB fails
        this.profiles = [this.createDefaultProfile(PROFILE_TYPE.PRIMARY)];
        this.currentProfile = this.profiles[0];
        this.initialized = true;
        return;
      }
      
      try {
        // Race the profile loading against the timeout
        await Promise.race([
          this.loadProfiles(signal),
          timeoutPromise
        ]);
      } catch (error) {
        // Enhanced error logging with proper serialization to avoid empty object displays
        const errorMessage = error instanceof Error ? error.message : String(error);
        const errorStack = error instanceof Error ? error.stack : 'No stack trace';
        
        loggingService.error('profile', `Profile loading error: ${errorMessage}`);
        // Detailed logging only in development
        
        // Create a sensible default profile if timeout or other error
        this.profiles = [this.createDefaultProfile(PROFILE_TYPE.PRIMARY)];
        this.currentProfile = this.profiles[0];
        this.initialized = true;
        this.initializing = false;
        return;
      }
      
      // Set current profile to primary user or first available profile
      if (this.profiles.length > 0) {
        const primaryProfile = this.profiles.find(profile => profile.type === 'primary');
        this.currentProfile = primaryProfile || this.profiles[0];
      } else {
        // Fallback: Create default profiles if none were loaded
        loggingService.info('profile', 'No profiles found, creating defaults');
        // No direct console in production
        const primaryProfile = this.createDefaultProfile(PROFILE_TYPE.PRIMARY);
        
        // Add retry mechanism for saving the profile
        try {
          await this.saveProfile(primaryProfile);
        } catch (saveError) {
          loggingService.warn('profile', `Failed to save primary profile, retrying once: ${saveError}`);
          // No direct console in production
          // Wait briefly and retry
          await new Promise(resolve => setTimeout(resolve, 500));
          await this.saveProfile(primaryProfile);
        }
        
        this.profiles = [primaryProfile];
        this.currentProfile = primaryProfile;
      }
      
      this.initialized = true;
      loggingService.info('profile', `Initialization complete with ${this.profiles.length} profiles`);
      // No direct console in production
    } catch (error) {
      // Enhanced error logging with proper error serialization
      const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error';
      const errorStack = error instanceof Error ? error.stack : undefined;
      
      // Log with detailed error information to make debugging easier
      loggingService.error('profile', `UserProfileService initialization failed: ${errorMessage}`);
      // Detailed error only in logging service to prevent production logs
      // Additional error details already logged above
      
      // Create emergency default profile to allow app to function
      try {
        const emergencyProfile = this.createDefaultProfile(PROFILE_TYPE.PRIMARY);
        this.profiles = [emergencyProfile];
        this.currentProfile = emergencyProfile;
        loggingService.warn('profile', 'Created emergency profile after initialization failure');
      } catch (fallbackError) {
        const fbErrorMsg = fallbackError instanceof Error ? fallbackError.message : 'Unknown error';
        loggingService.error('profile', `Failed to create emergency profile: ${fbErrorMsg}`);
      }
      
      // Mark as initialized even on error to prevent repeated failures
      this.initialized = true;
      
      // Don't throw the error - just return as we've set up an emergency profile
      return;
    } finally {
      // Always clear the initializing flag and clean up resources
      this.initializing = false;
      
      // Execute all cleanup actions
      cleanupActions.forEach(cleanup => cleanup());
    }
  }

  /**
   * Load all user profiles from the database
   * @param signal Optional AbortSignal to allow cancellation of loading
   */
  private async loadProfiles(signal?: AbortSignal): Promise<void> {
    const startTime = Date.now();
    loggingService.info('profile', 'Starting profile load');
    
    // Check if the operation was aborted - don't throw but create a fallback
    if (signal?.aborted) {
      loggingService.info('profile', 'Profile loading aborted, using fallback profile');
      const fallbackProfile = this.createDefaultProfile(PROFILE_TYPE.PRIMARY);
      this.profiles = [fallbackProfile];
      this.currentProfile = fallbackProfile;
      return;
    }
    
    try {
      const profiles = await databaseService.getData(this.COLLECTION_NAME) as UserProfile[] | null;
      this.profiles = profiles || [];
      const loadTime = Date.now() - startTime;
      loggingService.info('profile', `Profiles loaded in ${loadTime}ms, count: ${this.profiles.length}`);
      
      // Check if the operation was aborted before creating default profiles
      if (signal?.aborted) {
        loggingService.info('profile', 'Profile creation aborted');
        return;
      }
      
      // Ensure there's at least one primary profile
      if (this.profiles.length === 0) {
        // Create primary user profile
        const primaryProfile = this.createDefaultProfile(PROFILE_TYPE.PRIMARY);
        
        try {
          await this.saveProfile(primaryProfile);
          this.profiles.push(primaryProfile);
          this.currentProfile = primaryProfile;
          loggingService.debug('profile', 'Created default primary profile');
        } catch (saveError) {
          const errorMessage = saveError instanceof Error ? saveError.message : 'Unknown error';
          loggingService.error('profile', `Failed to save primary profile: ${errorMessage}`);
          
          // Still add to memory even if save fails
          this.profiles.push(primaryProfile);
          this.currentProfile = primaryProfile;
        }
      } else {
        // Set current profile to primary profile if available
        const primaryProfile = this.profiles.find(p => p.type === PROFILE_TYPE.PRIMARY);
        if (primaryProfile) {
          this.currentProfile = primaryProfile;
        } else {
          this.currentProfile = this.profiles[0];
        }
      }
    } catch (error) {
      // Ensure proper error logging with detailed information
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorStack = error instanceof Error ? error.stack : undefined;
      
      loggingService.warn('profile', `Error loading profiles from database: ${errorMessage}`);
      // Detailed logging only in development through loggingService
      
      // Create a fallback profile
      const fallbackProfile = this.createDefaultProfile(PROFILE_TYPE.PRIMARY);
      this.profiles = [fallbackProfile];
      this.currentProfile = fallbackProfile;
    }
  }
  
  /**
   * Create a default user profile for first-time users
   * @param profileType Type of profile to create ('primary' or 'dependent')
   * @returns New user profile
   */
  private createDefaultProfile(profileType: ProfileType): UserProfile {
    const now = new Date().toISOString();
    
    // CRITICAL: Always use the predefined user IDs ('u1', 'u2') instead of UUIDs
    // This prevents profile mismatch issues between localStorage and the profile store
    const profileId = profileType === PROFILE_TYPE.PRIMARY ? PROFILE_IDS.USER_1 : PROFILE_IDS.USER_2;
    
    return {
      id: profileId,
      name: profileType === PROFILE_TYPE.PRIMARY ? PROFILE_NAMES.USER_1 : PROFILE_NAMES.USER_2,
      type: profileType,
      plusTier: false, // Using Plus tier naming (not Pro)
      preferences: { // Using DEFAULT_USER_PREFERENCES
        theme: 'system',
        notifications: {
          medicationReminders: true,
          reminderLeadTime: 30,
          refillReminders: true,
          refillReminderDays: 7
        },
        accessibility: {
          fontSize: 1,
          highContrast: false,
          reduceMotion: false
        },
        calendar: {
          defaultView: 'day',
          firstDayOfWeek: 0,
          timeFormat: '12h'
        }
      },
      createdAt: now,
      updatedAt: now,
      sharingEnabled: false,
      helperPermissions: { // Using DEFAULT_HELPER_PERMISSIONS
        viewSchedule: false,
        viewMedications: false,
        markCompleted: false,
        sharedMedicationIds: []
      }
    };
  }

  /**
   * Get all user profiles
   */
  async getAllProfiles(): Promise<UserProfile[]> {
    await this.ensureInitialized();
    return [...this.profiles];
  }

  /**
   * Get a profile by its ID
   * @param id Profile ID to find
   */
  async getProfileById(id: string): Promise<UserProfile | null> {
    await this.ensureInitialized();
    return this.profiles.find(profile => profile.id === id) || null;
  }

  /**
   * Get the current active profile
   */
  async getCurrentProfile(): Promise<UserProfile | null> {
    await this.ensureInitialized();
    return this.currentProfile;
  }

  /**
   * Set the current active profile by ID
   * @param id ID of the profile to set as current
   */
  async setCurrentProfile(id: string): Promise<UserProfile | null> {
    await this.ensureInitialized();
    
    const profile = this.profiles.find(p => p.id === id);
    if (!profile) {
      loggingService.warn('profile', `Attempted to set current profile to non-existent profile: ${id}`);
      return null;
    }
    
    this.currentProfile = profile;
    
    // Track profile switching for analytics
    analyticsService.trackEvent('profile_switched', {
      profileType: profile.type,
      isPlusTier: profile.plusTier || false // Using Plus tier naming
    });
    
    return profile;
  }

  /**
   * Create a new user profile
   * @param profileData Partial profile data to create a profile with
   */
  async createProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
    await this.ensureInitialized();
    
    const now = new Date().toISOString();
    
    // Create default preferences and permissions objects to ensure type safety
    const defaultPreferences: UserPreferences = {
      theme: 'system',
      notifications: {
        medicationReminders: true,
        reminderLeadTime: 30,
        refillReminders: true,
        refillReminderDays: 7
      },
      accessibility: {
        fontSize: 1,
        highContrast: false,
        reduceMotion: false
      },
      calendar: {
        defaultView: 'day',
        firstDayOfWeek: 0,
        timeFormat: '12h'
      }
    };
    
    const defaultHelperPermissions: HelperPermissions = {
      viewSchedule: false,
      viewMedications: false,
      markCompleted: false,
      sharedMedicationIds: []
    };
    
    // Create a sanitized copy of profileData, removing any potential id/createdAt/updatedAt
    // to avoid duplicate keys during object spreads
    const { id, createdAt, updatedAt, ...sanitizedProfileData } = profileData;
    
    // First create a base profile with default values
    // CRITICAL: Determine the correct profile ID based on the profile type
    // Always use the predefined profile IDs ('u1', 'u2') instead of UUIDs to avoid inconsistencies
    let profileId: string;
    
    if (id && (id === PROFILE_IDS.USER_1 || id === PROFILE_IDS.USER_2)) {
      // Use the provided ID if it's one of our predefined IDs
      profileId = id;
    } else {
      // Otherwise, determine the appropriate ID based on profile type
      // For primary users, use 'u1'; for dependents, use 'u2'
      const profileType = profileData.type || PROFILE_TYPE.PRIMARY;
      profileId = profileType === PROFILE_TYPE.PRIMARY ? PROFILE_IDS.USER_1 : PROFILE_IDS.USER_2;
    }
    
    const baseProfile: UserProfile = {
      id: profileId,
      name: profileData.name || (profileId === PROFILE_IDS.USER_1 ? PROFILE_NAMES.USER_1 : PROFILE_NAMES.USER_2),
      type: profileData.type || PROFILE_TYPE.PRIMARY,
      plusTier: profileData.plusTier || false, // Using Plus tier naming
      preferences: defaultPreferences,
      createdAt: createdAt || now,
      updatedAt: now,
      sharingEnabled: false,
      helperPermissions: defaultHelperPermissions
    };
    
    // Then merge with any additional profile data, but preserve our critical fields
    const newProfile: UserProfile = {
      ...baseProfile,
      ...sanitizedProfileData
    };
    
    // Save to database
    await this.saveProfile(newProfile);
    
    // Update profiles list
    this.profiles.push(newProfile);
    
    // If this is the first profile, set it as current
    if (this.profiles.length === 1) {
      this.currentProfile = newProfile;
    }
    
    return newProfile;
  }

  /**
   * Update an existing profile
   * @param id ID of the profile to update
   * @param data New profile data (partial)
   */
  async updateProfile(id: string, data: Partial<UserProfile>): Promise<UserProfile | null> {
    await this.ensureInitialized();
    
    const profileIndex = this.profiles.findIndex(p => p.id === id);
    if (profileIndex === -1) {
      loggingService.warn('profile', `Attempted to update non-existent profile: ${id}`);
      return null;
    }
    
    // Get the existing profile
    const existingProfile = this.profiles[profileIndex];
    
    // Create updated profile with new data, preserving id and creation date
    const updatedProfile: UserProfile = {
      ...existingProfile,
      ...data,
      id, // Ensure ID remains the same
      createdAt: existingProfile.createdAt, // Preserve creation date
      updatedAt: new Date().toISOString() // Update the timestamp
    };
    
    // Save to database
    await this.saveProfile(updatedProfile);
    
    // Update in-memory profile array
    this.profiles[profileIndex] = updatedProfile;
    
    // Update currentProfile if it's the one being updated
    if (this.currentProfile?.id === id) {
      this.currentProfile = updatedProfile;
    }
    
    return updatedProfile;
  }

  /**
   * Delete a profile
   * @param id - ID of the profile to delete
   */
  async deleteProfile(id: string): Promise<boolean> {
    await this.ensureInitialized();
    
    // Don't allow deleting the primary profile if it's the only profile
    const primaryProfiles = this.profiles.filter(p => p.type === 'primary');
    const profileToDelete = this.profiles.find(p => p.id === id);
    
    if (!profileToDelete) return false;
    
    if (profileToDelete.type === 'primary' && primaryProfiles.length <= 1 && this.profiles.length > 1) {
      throw new Error('Cannot delete the only primary profile while dependent profiles exist');
    }
    
    try {
      await databaseService.deleteDataById(this.COLLECTION_NAME, id);
      this.profiles = this.profiles.filter(p => p.id !== id);
      
      // If we deleted the current profile, switch to another one
      if (this.currentProfile?.id === id) {
        this.currentProfile = this.profiles.length > 0 ? this.profiles[0] : null;
      }
      
      return true;
    } catch (error) {
      
      return false;
    }
  }

  /**
   * Save a user profile to the database
   * @param profile Profile to save
   */
  private async saveProfile(profile: UserProfile): Promise<void> {
    // Ensure timestamps are updated
    profile.updatedAt = new Date().toISOString();
    
    // Ensure the profile has the proper naming for Plus tier
    if (profile.plusTier) {
      // Ensure we use 'Plus' terminology instead of 'Pro'
      if ((profile as any).proTier) {
        delete (profile as any).proTier;
        profile.plusTier = true;
      }
    }
    
    try {
      await databaseService.saveData(this.COLLECTION_NAME, profile.id, profile);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      loggingService.error('profile', `Failed to save profile: ${errorMessage}`);
      throw error;
    }
  }

  /**
   * Ensure the service is initialized before operations
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
  }

  /**
   * Update a profile's Plus tier status
   * @param profileId ID of the profile to update
   * @param isPlusTier New Plus tier status
   */
  async updatePlusTierStatus(profileId: string, isPlusTier: boolean): Promise<boolean> {
    await this.ensureInitialized();
    
    const profile = await this.getProfileById(profileId);
    if (!profile) {
      loggingService.warn('profile', `Attempted to update Plus tier status for non-existent profile: ${profileId}`);
      return false;
    }
    
    try {
      // Track tier change in analytics
      analyticsService.trackEvent('plus_tier_status_changed', {
        previous: profile.plusTier || false,
        current: isPlusTier,
        profileType: profile.type
      });
      
      // Update profile
      await this.updateProfile(profileId, { plusTier: isPlusTier });
      return true;
    } catch (error) {
      loggingService.error('profile', `Failed to update Plus tier status: ${error}`);
      return false;
    }
  }
}

// Create singleton instance
export const userProfileService = new UserProfileService();
